import React, { useRef, useState, useEffect } from 'react';
import { MessageSquare, Send, X, Minimize2, Maximize2 } from 'lucide-react';

/**
 * Composant ChatPanel - Dialogue flottant déplaçable et redimensionnable
 */
const ChatPanel = ({
  isOpen,
  onClose,
  onOpen,
  messages,
  newMessage,
  setNewMessage,
  sendMessage,
  handleTyping,
  formatTime,
  isTyping,
  client,
  messagesEndRef,
  chatInputRef,
  socketConnected,
  isMinimized = false,
  onMinimize
}) => {
  const windowRef = useRef(null);
  const headerRef = useRef(null);
  const [position, setPosition] = useState({ x: window.innerWidth - 380, y: 100 });
  const [size, setSize] = useState({ width: 360, height: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}')._id;

  // Gestion du drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - (isMinimized ? 60 : size.height);
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, size, isMinimized]);

  // Gestion du resize
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = resizeStart.width + (e.clientX - resizeStart.x);
      const newHeight = resizeStart.height + (e.clientY - resizeStart.y);
      
      const minWidth = 300;
      const maxWidth = window.innerWidth - position.x;
      const minHeight = isMinimized ? 60 : 300;
      const maxHeight = window.innerHeight - position.y;
      
      setSize({
        width: Math.max(minWidth, Math.min(newWidth, maxWidth)),
        height: Math.max(minHeight, Math.min(newHeight, maxHeight))
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, position, isMinimized]);

  // Bouton flottant en bas à droite quand le chat est fermé
  if (!isOpen) {
    const unreadCount = messages.length;
    
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onOpen) {
              onOpen();
            }
          }}
          className="relative w-14 h-14 bg-blue-500 hover:bg-blue-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center cursor-pointer"
          title="Ouvrir le chat"
        >
          <MessageSquare className="h-6 w-6 text-white" />
          
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          
          <div
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
              socketConnected ? 'bg-green-400' : 'bg-red-400'
            }`}
            title={socketConnected ? 'Connecté' : 'Déconnecté'}
          />
        </button>
      </div>
    );
  }

  // Dialogue flottant
  return (
    <div
      ref={windowRef}
      className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl flex flex-col z-[100]"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: isMinimized ? '60px' : `${size.height}px`,
        transition: isDragging || isResizing ? 'none' : 'all 0.2s ease',
      }}
    >
      {/* Header - Zone de drag */}
      <div
        ref={headerRef}
        className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg select-none"
      >
        <div 
          className="flex items-center gap-2 flex-1 cursor-move"
          onMouseDown={(e) => {
            // Ne déclencher le drag que si on clique sur cette zone, pas sur les boutons
            if (e.target.closest('button')) {
              return;
            }
            setIsDragging(true);
            setDragStart({
              x: e.clientX - position.x,
              y: e.clientY - position.y
            });
          }}
        >
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Messages</h3>
          <div
            className={`w-2 h-2 rounded-full ${socketConnected ? 'bg-green-500' : 'bg-red-500'}`}
            title={socketConnected ? 'Connecté' : 'Déconnecté'}
          />
        </div>
        <div className="flex items-center gap-1">
          {onMinimize && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMinimize?.();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 active:scale-95 transition-all duration-150 p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer flex items-center justify-center"
              title={isMinimized ? "Restaurer" : "Réduire"}
              type="button"
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </button>
          )}
        <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Close button clicked');
              if (onClose) {
                onClose();
              }
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 active:scale-95 transition-all duration-150 p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer flex items-center justify-center"
          title="Fermer le chat"
            type="button"
        >
            <X className="h-4 w-4" />
        </button>
        </div>
      </div>

      {/* Zone des messages - Masquée si minimisé */}
      {!isMinimized && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white dark:bg-gray-800" style={{ minHeight: 0 }}>
        {messages.length === 0 ? (
              <div className="text-center text-gray-400 dark:text-gray-500 text-sm mt-12">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-gray-500 dark:text-gray-400">Aucun message</p>
          </div>
        ) : (
              messages.map((msg, index) => {
            const isSystem = msg.messageType === 'system';
            const isOwn =
              msg.senderUid === client?.uid ||
              (msg.senderUserId && currentUserId && msg.senderUserId.toString() === currentUserId.toString());
                
                const prevMsg = index > 0 ? messages[index - 1] : null;
                const isSameSender = prevMsg && 
                  !prevMsg.messageType === 'system' &&
                  ((prevMsg.senderUid === client?.uid && isOwn) ||
                   (prevMsg.senderUserId && msg.senderUserId && 
                    prevMsg.senderUserId.toString() === msg.senderUserId.toString()));
                
                const getInitials = (name) => {
                  if (!name) return '?';
                  const parts = name.trim().split(' ');
                  if (parts.length >= 2) {
                    return (parts[0][0] + parts[1][0]).toUpperCase();
                  }
                  return name.substring(0, 2).toUpperCase();
                };

            return (
              <div
                key={msg.id}
                    className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'} ${isSystem ? 'justify-center' : ''}`}
                  >
                    {!isSystem && !isSameSender && (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                        isOwn 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-400 text-white'
                      }`}>
                        {getInitials(msg.senderName)}
                      </div>
                    )}
                    {!isSystem && isSameSender && (
                      <div className="w-8 flex-shrink-0" />
                    )}
                    <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} flex-1 min-w-0 ${isSystem ? 'w-full items-center' : ''}`}>
                      {!isSystem && !isSameSender && (
                        <div className={`flex items-center gap-2 mb-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {msg.senderName}
                    </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                )}
                <div
                        className={`rounded-lg px-3 py-2 ${
                    isSystem
                            ? 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 text-xs text-center w-full'
                      : isOwn
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }`}
                >
                        <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                    {msg.message}
                  </p>
                      </div>
                      {!isSystem && isSameSender && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {formatTime(msg.timestamp)}
                        </span>
                      )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Zone de saisie */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg">
        <div className="flex items-center gap-2">
          <input
            ref={chatInputRef}
            type="text"
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
                placeholder="Envoyer un message"
                className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim()}
                className="p-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                title="Envoyer"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
        </>
      )}

      {/* Handle de redimensionnement */}
      {!isMinimized && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-transparent hover:bg-blue-500 opacity-0 hover:opacity-100 transition-opacity"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsResizing(true);
            setResizeStart({
              x: e.clientX,
              y: e.clientY,
              width: size.width,
              height: size.height
            });
          }}
          style={{
            clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
          }}
        />
      )}
    </div>
  );
};

export default ChatPanel;
